import React, { useEffect, useRef } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { store } from '../../../config/firebase';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import OverlayScoreboard from '../../ui/gameControls/overlayScoreboard';
import ScoringDisplay from '../../ui/playerDisplay/scoringDisplay';
import { useColorMode } from '../../../hooks/colorModeProvider';
import { GameStatus } from '../../../constants/gameConstants';
import { liveGameDocs } from '../../../helpers/games';

const COMPLETED_HANG_MS = 2 * 60 * 1000;

const getUpdatedAtMs = (value) => {
  if (!value) {
    return 0;
  }

  if (typeof value.toDate === 'function') {
    return value.toDate().getTime();
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const LiveScorecardEvent = () => {
  const { eventId } = useParams();
  const { mode, setMode } = useColorMode();
  const initialModeRef = useRef(mode);
  const clearCompletedTimeoutRef = useRef(null);
  const [fsGame, setFSGame] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  useEffect(() => {
    const previousMode = initialModeRef.current;
    setMode('dark');

    return () => {
      setMode(previousMode);
    };
  }, [setMode]);

  useEffect(() => {
    const gamesRef = collection(store, 'games');
    const q = query(gamesRef, where('event', '==', eventId.toString()));

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const now = Date.now();
        // liveGameDocs covers the deletedAt marker as well as the older
        // `deleted` flag this used to test on its own.
        const allGames = liveGameDocs(querySnapshot).map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));

        const activeGames = allGames
          .filter((game) => [GameStatus.IN_PROGRESS, GameStatus.PLAYOFF].includes(game.status))
          .sort((a, b) => {
            if (Boolean(a.live) !== Boolean(b.live)) {
              return a.live ? -1 : 1;
            }

            return getUpdatedAtMs(b.updatedAt) - getUpdatedAtMs(a.updatedAt);
          });

        if (activeGames.length > 0) {
          setFSGame(activeGames[0]);
        } else {
          const recentlyCompletedGames = allGames
            .filter(
              (game) =>
                game.status === GameStatus.COMPLETE &&
                now - getUpdatedAtMs(game.updatedAt) <= COMPLETED_HANG_MS
            )
            .sort((a, b) => getUpdatedAtMs(b.updatedAt) - getUpdatedAtMs(a.updatedAt));

          setFSGame(recentlyCompletedGames[0] ?? null);
        }

        setLoading(false);
      },
      () => {
        setFSGame(null);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [eventId]);

  useEffect(() => {
    if (clearCompletedTimeoutRef.current) {
      clearTimeout(clearCompletedTimeoutRef.current);
      clearCompletedTimeoutRef.current = null;
    }

    if (!fsGame || fsGame.status !== GameStatus.COMPLETE) {
      return undefined;
    }

    const elapsed = Date.now() - getUpdatedAtMs(fsGame.updatedAt);
    const remaining = Math.max(0, COMPLETED_HANG_MS - elapsed);

    if (remaining === 0) {
      setFSGame(null);
      return undefined;
    }

    clearCompletedTimeoutRef.current = setTimeout(() => {
      setFSGame((currentGame) => {
        if (!currentGame) {
          return null;
        }

        if (currentGame.id !== fsGame.id || currentGame.status !== GameStatus.COMPLETE) {
          return currentGame;
        }

        return null;
      });
    }, remaining);

    return () => {
      if (clearCompletedTimeoutRef.current) {
        clearTimeout(clearCompletedTimeoutRef.current);
        clearCompletedTimeoutRef.current = null;
      }
    };
  }, [fsGame]);

  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        sx={{ backgroundColor: 'transparent', color: 'text.primary' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!fsGame) {
    return <Box minHeight="100vh" sx={{ backgroundColor: 'transparent' }} />;
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      px={2}
      py={3}
      sx={{
        backgroundColor: 'transparent',
        color: 'text.primary'
      }}>
      <Typography variant="h4" fontWeight={800} textAlign="center" mb={1.5}>
        {fsGame.name ?? 'Current Live Game Scorecard'}
      </Typography>
      <Box
        sx={{
          width: '100%',
          maxWidth: 1200,
          p: 2,
          mb: 2,
          borderRadius: 3,
          backgroundColor: 'rgba(10, 18, 32, 0.9)',
          backdropFilter: 'blur(2px)'
        }}>
        <ScoringDisplay game={fsGame} />
      </Box>
      {fsGame.sets?.length && (
        <Box
          sx={{
            width: '100%',
            maxWidth: 1200,
            p: 2,
            borderRadius: 3,
            backgroundColor: 'rgba(10, 18, 32, 0.9)',
            backdropFilter: 'blur(2px)'
          }}>
          <OverlayScoreboard game={fsGame} />
        </Box>
      )}
    </Box>
  );
};

export default LiveScorecardEvent;
